import { CanvasImageValue } from '@seaters/constants';
import React, { type JSX } from 'react';
import { DeleteOutlined, PictureOutlined } from '@ant-design/icons';
import {
  Image,
  Spin,
  Upload,
  theme,
  UploadProps,
  Space,
  FormItemProps,
  Row,
  Form,
  Col,
  Typography,
} from 'antd';
import {
  Button,
  baseColor400,
  colorFillQuaternary,
  secondaryColor,
} from '@seaters/ui';
import styles from './styles.module.css';
import { useTranslation } from 'react-i18next';
import { UploadChangeParam, UploadFile } from 'antd/es/upload';

const { Dragger } = Upload;
const { Text } = Typography;

interface ImageUploaderProps {
  color?: string;
  width: number;
  height: number;
  /** Shows the picture and the drop area as a square this wide (px); the crop keeps `width` x `height`. */
  previewSize?: number;
  remove: () => void;
  /** Hides the remove button, for a picture the caller cannot delete. */
  hideRemove?: boolean;
  className: string;
  isLoading?: boolean;
  handleChange: (info: UploadChangeParam<UploadFile>) => void;
  beforeUpload?: UploadProps['beforeUpload'];
  uploadText?: string | JSX.Element;
  accept?: UploadProps['accept'];
  formItemProps?: FormItemProps;
  imageOrCanvas: CanvasImageValue | string | null | undefined;
  customRequest: UploadProps['customRequest'];
  listType?: UploadProps['listType'];
  sideContent?: React.ReactNode;
  size: string;
}

export const ImageUploader = ({
  color,
  handleChange,
  remove,
  hideRemove = false,
  width,
  height,
  previewSize,
  isLoading = false,
  beforeUpload,
  uploadText,
  accept,
  formItemProps,
  customRequest,
  imageOrCanvas,
  sideContent,
  listType,
  size,
}: ImageUploaderProps) => {
  const { token } = theme.useToken();
  const { t } = useTranslation();

  const boxWidth = previewSize ?? width;
  const isSmallDragAndDropArea = boxWidth < 150 || height < 150;
  const isPictureCircle = listType === 'picture-circle';
  const isSquarePreview = previewSize !== undefined;
  // What is left of the square inside its 1px border and 8px padding.
  const pictureSize = boxWidth - 2 * (1 + 8);

  const renderUploadContent = () => {
    if (isSmallDragAndDropArea || isPictureCircle) {
      return (
        <div>
          <PictureOutlined
            className="text-[28px]"
            style={{ color: color || secondaryColor }}
          />
          <Text className="text-[10px] block">{size}</Text>
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center justify-center gap-4 p-4">
        <div className="size-12 flex items-center justify-center">
          <PictureOutlined
            className="text-[42px]"
            style={{ color: color || secondaryColor }}
          />
        </div>

        <div className="flex flex-col items-center gap-1">
          <Text className="text-sm text-center leading-[22px]">
            {uploadText || t('drop_file_info_message')}
          </Text>
        </div>
      </div>
    );
  };

  return (
    <Row gutter={24} style={{ maxWidth: '100%' }} align="middle">
      <Col>
        <Spin wrapperClassName={styles.spin} spinning={isLoading}>
          <Space direction="vertical" size="small" style={{ maxWidth: '100%' }}>
            <div
              style={{
                width: isPictureCircle ? 102 : boxWidth,
                height: isPictureCircle ? 102 : 'unset',
                maxWidth: '100%',
                aspectRatio: isSquarePreview ? 1 : width / height,
                // A square preview frames the picture: 1px border, 8px padding, rounded.
                ...(isSquarePreview && imageOrCanvas
                  ? {
                      boxSizing: 'border-box',
                      border: `1px solid ${token.colorBorder}`,
                      borderRadius: 8,
                      padding: 8,
                      // A flex box takes the upload wrapper's line gap out from under the picture.
                      display: 'flex',
                    }
                  : {}),
              }}
            >
              {imageOrCanvas && isSquarePreview ? (
                // Clicking the picture picks a replacement; it is also the only way to change a
                // picture the caller cannot remove.
                <Upload
                  multiple={false}
                  customRequest={customRequest}
                  onChange={handleChange}
                  beforeUpload={beforeUpload}
                  accept={accept}
                  showUploadList={false}
                  name={formItemProps?.name}
                  id={formItemProps?.id}
                >
                  <img
                    alt=""
                    src={
                      typeof imageOrCanvas !== 'string'
                        ? imageOrCanvas.toDataURL()
                        : imageOrCanvas
                    }
                    style={{
                      display: 'block',
                      width: pictureSize,
                      height: pictureSize,
                      objectFit: 'cover',
                      borderRadius: 4,
                      cursor: 'pointer',
                    }}
                  />
                </Upload>
              ) : imageOrCanvas ? (
                <Image
                  width={'100%'}
                  src={
                    imageOrCanvas && typeof imageOrCanvas !== 'string'
                      ? imageOrCanvas?.toDataURL()
                      : imageOrCanvas
                  }
                  style={{
                    borderRadius: listType === 'picture-circle' ? '50%' : 12,
                    maxWidth: '100%',
                    border: `1px solid ${baseColor400}`,
                  }}
                />
              ) : isPictureCircle ? (
                <Upload
                  multiple={false}
                  customRequest={customRequest}
                  listType="picture-circle"
                  onChange={handleChange}
                  beforeUpload={beforeUpload}
                  accept={accept}
                  showUploadList={false}
                  name={formItemProps?.name}
                  id={formItemProps?.id}
                  style={{
                    ...(formItemProps?.validateStatus === 'error'
                      ? { borderColor: token.colorError }
                      : {}),
                  }}
                >
                  {renderUploadContent()}
                </Upload>
              ) : (
                <Dragger
                  multiple={false}
                  customRequest={customRequest}
                  onChange={handleChange}
                  beforeUpload={beforeUpload}
                  accept={accept}
                  showUploadList={false}
                  name={formItemProps?.name}
                  id={formItemProps?.id}
                  rootClassName={isSquarePreview ? '!h-full' : undefined}
                  style={{
                    backgroundColor: colorFillQuaternary,
                    ...(isSquarePreview ? { height: '100%' } : {}),
                    ...(formItemProps?.validateStatus === 'error'
                      ? { borderColor: token.colorError }
                      : {}),
                  }}
                >
                  {renderUploadContent()}
                </Dragger>
              )}
            </div>
            {imageOrCanvas && !hideRemove ? (
              <Button
                danger
                type="text"
                onClick={remove}
                icon={<DeleteOutlined rev={undefined} />}
              >
                {t('remove_image_button_text')}
              </Button>
            ) : null}
          </Space>
        </Spin>
      </Col>
      {sideContent && (
        <Col xs={16} md={16} lg={12} className={styles.centeredCol}>
          <Form.Item>
            <Text type="secondary" className="text-xs w-full">
              {sideContent}
            </Text>
          </Form.Item>
        </Col>
      )}
      {formItemProps?.help && (
        <span style={{ color: token.colorError }}> {formItemProps?.help}</span>
      )}
    </Row>
  );
};
